import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Form, Spinner } from 'react-bootstrap';
import { apiGet, apiPut } from '../services/apiClient';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

const statusVariant = {
  active: 'success',
  inactive: 'secondary',
};

const normalizeEnv = (value) => {
  const raw = String(value || '').toLowerCase().trim();
  if (raw === 'prod' || raw === 'production') return 'prod';
  if (raw === 'sandbox') return 'sandbox';
  return '';
};

const ChannelProviderDetailPage = ({ token }) => {
  const { id } = useParams();
  const [provider, setProvider] = useState(null);
  const [integration, setIntegration] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [form, setForm] = useState({
    enabled: true,
    env: 'prod',
    base_url: '',
    api_key: '',
    channel_id: '',
    booking_status_path: '',
    booking_cancel_path: '',
    webhook_secret: '',
    webhook_header: '',
  });

  const load = async () => {
    setLoading(true);
    setError('');
    setNotice('');
    try {
      const data = await apiGet(`/api/admin/channel/providers/${id}`, token);
      setProvider(data?.provider || null);
      setIntegration(data?.integration || null);
      setForm({
        enabled: data?.integration?.enabled ?? true,
        env: normalizeEnv(data?.integration?.env) || 'prod',
        base_url: data?.integration?.base_url || '',
        api_key: '',
        channel_id: '',
        booking_status_path: data?.integration?.booking_status_path || '',
        booking_cancel_path: data?.integration?.booking_cancel_path || '',
        webhook_secret: '',
        webhook_header: data?.integration?.webhook_header || '',
      });
    } catch (err) {
      setError(err.message || 'Failed to load provider');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const webhookUrl = useMemo(() => {
    if (!provider?.key) return '';
    return `${API_BASE}/api/providers/${provider.key}/webhook`;
  }, [provider]);

  const handleSave = async (event) => {
    event.preventDefault();
    setError('');
    setNotice('');
    setSaving(true);
    try {
      const payload = {
        enabled: !!form.enabled,
        env: form.env,
      };
      if (form.base_url.trim()) payload.base_url = form.base_url.trim();
      if (form.api_key.trim()) payload.api_key = form.api_key.trim();
      if (form.channel_id.trim()) payload.channel_id = form.channel_id.trim();
      if (form.booking_status_path.trim()) {
        payload.booking_status_path = form.booking_status_path.trim();
      }
      if (form.booking_cancel_path.trim()) {
        payload.booking_cancel_path = form.booking_cancel_path.trim();
      }
      if (form.webhook_secret.trim()) payload.webhook_secret = form.webhook_secret.trim();
      if (form.webhook_header.trim()) payload.webhook_header = form.webhook_header.trim();

      const res = await apiPut(`/api/admin/channel/providers/${id}`, payload, token);
      setIntegration(res?.integration || null);
      setNotice('Credentials updated.');
      setForm((prev) => ({
        ...prev,
        api_key: '',
        channel_id: '',
        webhook_secret: '',
      }));
    } catch (err) {
      setError(err.message || 'Failed to update provider');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !provider) {
    return (
      <div className="py-4 text-center">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!provider) {
    return <Alert variant="danger">Provider not found.</Alert>;
  }

  return (
    <div className="p-4 bg-white rounded shadow-sm">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h4 className="mb-0">{provider.name}</h4>
          <small className="text-muted">{provider.key}</small>
        </div>
        <Badge bg={statusVariant[provider.status] || 'secondary'}>{provider.status}</Badge>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {notice && <Alert variant="success">{notice}</Alert>}

      <div className="row g-3">
        <div className="col-lg-7">
          <Card>
            <Card.Body>
              <Card.Title className="h6">Provider credentials</Card.Title>
              <Form onSubmit={handleSave}>
                <Form.Check
                  type="switch"
                  id="provider-enabled"
                  label="Enable integration"
                  checked={!!form.enabled}
                  onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
                />
                <div className="row g-2 mt-2">
                  <Form.Group className="col-md-6">
                    <Form.Label>Environment</Form.Label>
                    <Form.Select
                      value={form.env}
                      onChange={(e) => setForm((prev) => ({ ...prev, env: e.target.value }))}
                    >
                      <option value="sandbox">sandbox</option>
                      <option value="prod">prod</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="col-md-6">
                    <Form.Label>API Base URL</Form.Label>
                    <Form.Control
                      value={form.base_url}
                      placeholder="https://api.elivaas.com/api"
                      onChange={(e) => setForm((prev) => ({ ...prev, base_url: e.target.value }))}
                    />
                  </Form.Group>
                </div>

                <div className="row g-2 mt-2">
                  <Form.Group className="col-md-6">
                    <Form.Label>API key</Form.Label>
                    <Form.Control
                      value={form.api_key}
                      placeholder={integration?.api_key_masked || 'Enter API key'}
                      onChange={(e) => setForm((prev) => ({ ...prev, api_key: e.target.value }))}
                    />
                  </Form.Group>
                  <Form.Group className="col-md-6">
                    <Form.Label>Channel ID</Form.Label>
                    <Form.Control
                      value={form.channel_id}
                      placeholder={integration?.channel_id_masked || 'Enter channel ID'}
                      onChange={(e) => setForm((prev) => ({ ...prev, channel_id: e.target.value }))}
                    />
                  </Form.Group>
                </div>

                <div className="row g-2 mt-2">
                  <Form.Group className="col-md-6">
                    <Form.Label>Booking status path</Form.Label>
                    <Form.Control
                      value={form.booking_status_path}
                      placeholder="/v1/bookings/{id}/status"
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, booking_status_path: e.target.value }))
                      }
                    />
                  </Form.Group>
                  <Form.Group className="col-md-6">
                    <Form.Label>Booking cancel path</Form.Label>
                    <Form.Control
                      value={form.booking_cancel_path}
                      placeholder="/v1/bookings/{id}/cancel"
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, booking_cancel_path: e.target.value }))
                      }
                    />
                  </Form.Group>
                </div>

                <div className="row g-2 mt-2">
                  <Form.Group className="col-md-6">
                    <Form.Label>Webhook secret</Form.Label>
                    <Form.Control
                      value={form.webhook_secret}
                      placeholder={integration?.webhook_secret_masked || 'Enter secret (optional)'}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, webhook_secret: e.target.value }))
                      }
                    />
                  </Form.Group>
                  <Form.Group className="col-md-6">
                    <Form.Label>Webhook header</Form.Label>
                    <Form.Control
                      value={form.webhook_header}
                      placeholder="x-elivaas-signature"
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, webhook_header: e.target.value }))
                      }
                    />
                  </Form.Group>
                </div>

                <Button type="submit" variant="primary" className="mt-3" disabled={saving}>
                  {saving ? 'Saving...' : 'Save credentials'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </div>

        <div className="col-lg-5">
          <Card className="mb-3">
            <Card.Body>
              <Card.Title className="h6">Webhook endpoint</Card.Title>
              <div className="text-muted small">Send provider events to:</div>
              <div className="fw-semibold">{webhookUrl || 'â€”'}</div>
              <div className="text-muted small mt-2">
                Header: {integration?.webhook_header || 'x-elivaas-signature'}
              </div>
            </Card.Body>
          </Card>
          <Card>
            <Card.Body>
              <Card.Title className="h6">Current config (masked)</Card.Title>
              <pre className="bg-light p-3 rounded small mb-0">
                {JSON.stringify(integration || {}, null, 2)}
              </pre>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ChannelProviderDetailPage;


